/**
 * Technology Action Handlers Hook
 *
 * Implements business logic for bulk technology operations:
 * - Adding notes to multiple technologies
 * - Adding tags to multiple technologies
 * - Removing tags from multiple technologies
 * - Exporting selected technologies to CSV
 *
 * Risk Level: Medium
 * - Async operations with error handling
 * - Multiple external dependencies
 * - Uses Promise.allSettled for graceful failure handling
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { useAddComment } from '@/hooks/useComment';
import type {
  ActionHandlerConfig,
  ActionHandlers,
} from '@/sections/technology/types/action.types';

/**
 * Custom hook for technology action business logic
 *
 * Provides handlers for bulk operations on technologies.
 * Uses Promise.allSettled to handle partial failures gracefully.
 *
 * Example usage:
 * ```tsx
 * const handlers = useTechnologyActionHandlers({
 *   selectedTechnologies,
 *   invalidateTechnologies,
 *   setConfirmStatus,
 * });
 *
 * await handlers.handleNote('Important technology');
 * await handlers.handleAddTag('production');
 * ```
 *
 * @param config - Configuration with selected technologies and callbacks
 * @returns Object containing action handlers
 */
export const useTechnologyActionHandlers = ({
  selectedTechnologies,
  invalidateTechnologies,
  setConfirmStatus,
  selectionState,
  onBulkRequested,
  onBulkUntagRequested,
}: ActionHandlerConfig): ActionHandlers => {
  const { mutateAsync: addComment } = useAddComment();
  const axios = useAxios();

  /**
   * Adds a note to all selected technologies
   *
   * Uses Promise.allSettled to ensure all operations complete even if some fail,
   * providing accurate user feedback about successes and failures.
   *
   * @param note - The note text to add
   */
  const handleNote = useCallback(
    async (note: string) => {
      const promises = selectedTechnologies.map(technology =>
        addComment({
          key: technology.key,
          name: technology.name || technology.product,
          comment: note,
          showSnackbar: false, // Suppress hook toast for bulk operations
        })
      );

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        invalidateTechnologies();
      }

      // Show accurate feedback based on results
      if (succeeded > 0 && failed === 0) {
        toast.success(
          succeeded === 1
            ? 'Note added to technology'
            : `Note added to ${succeeded} technologies`
        );
        setConfirmStatus({ type: 'success', count: succeeded });
      } else if (succeeded > 0 && failed > 0) {
        const successMsg =
          succeeded === 1
            ? 'Note added to technology'
            : `Note added to ${succeeded} technologies`;
        const failMsg =
          failed === 1
            ? 'failed for 1 technology'
            : `failed for ${failed} technologies`;
        toast.warning(`${successMsg}, but ${failMsg}`);
      } else {
        toast.error(
          failed === 1
            ? 'Failed to add note to technology'
            : `Failed to add note to ${failed} technologies`
        );
      }

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            technology:
              selectedTechnologies[i]?.name || selectedTechnologies[i]?.key,
            error: r.reason,
          }));
        console.error(
          '[useTechnologyActionHandlers] Failed to add note to technologies:',
          failedResults
        );
      }
    },
    [selectedTechnologies, addComment, invalidateTechnologies, setConfirmStatus]
  );

  /**
   * Adds a tag to all selected technologies
   *
   * Uses Promise.allSettled to ensure all operations complete even if some fail,
   * providing accurate user feedback about successes and failures.
   *
   * @param tag - The tag to add
   */
  const handleAddTag = useCallback(
    async (tag: string) => {
      if (selectionState && shouldUseBulk(selectionState) && onBulkRequested) {
        onBulkRequested(tag);
        return;
      }

      // Create promises for updating each technology
      const promises = selectedTechnologies.map(technology =>
        axios.put('/technology', {
          key: technology.key,
          tags: [...(technology.tags || []), tag],
        })
      );

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        invalidateTechnologies();
      }

      // Show accurate feedback based on results
      if (succeeded > 0 && failed === 0) {
        toast.success(
          `Tag "${tag}" added to ${succeeded} ${succeeded === 1 ? 'technology' : 'technologies'}`
        );
        setConfirmStatus({ type: 'success', count: succeeded });
      } else if (succeeded > 0 && failed > 0) {
        toast.warning(
          `Tag "${tag}" added to ${succeeded} ${succeeded === 1 ? 'technology' : 'technologies'}, ` +
            `but failed for ${failed} ${failed === 1 ? 'technology' : 'technologies'}`
        );
      } else {
        toast.error(
          `Failed to add tag to ${failed} ${failed === 1 ? 'technology' : 'technologies'}`
        );
      }

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            technology:
              selectedTechnologies[i]?.name || selectedTechnologies[i]?.key,
            error: r.reason,
          }));
        console.error(
          '[useTechnologyActionHandlers] Failed to add tag to technologies:',
          failedResults
        );
      }
    },
    [
      selectedTechnologies,
      axios,
      invalidateTechnologies,
      setConfirmStatus,
      selectionState,
      onBulkRequested,
    ]
  );

  /**
   * Removes a tag from all selected technologies
   *
   * Uses Promise.allSettled to ensure all operations complete even if some fail,
   * providing accurate user feedback about successes and failures.
   *
   * @param tag - The tag to remove
   */
  const handleRemoveTag = useCallback(
    async (tag: string) => {
      // Bulk path mirrors handleAddTag at :143-146
      if (
        selectionState &&
        shouldUseBulk(selectionState) &&
        onBulkUntagRequested
      ) {
        onBulkUntagRequested(tag);
        return;
      }

      // Create promises for updating each technology
      const promises = selectedTechnologies.map(technology =>
        axios.put('/technology', {
          key: technology.key,
          tags: (technology.tags || []).filter(t => t !== tag),
        })
      );

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        invalidateTechnologies();
      }

      // Show accurate feedback based on results
      if (succeeded > 0 && failed === 0) {
        toast.success(
          `Tag "${tag}" removed from ${succeeded} ${succeeded === 1 ? 'technology' : 'technologies'}`
        );
        setConfirmStatus({ type: 'success', count: succeeded });
      } else if (succeeded > 0 && failed > 0) {
        toast.warning(
          `Tag "${tag}" removed from ${succeeded} ${succeeded === 1 ? 'technology' : 'technologies'}, ` +
            `but failed for ${failed} ${failed === 1 ? 'technology' : 'technologies'}`
        );
      } else {
        toast.error(
          `Failed to remove tag from ${failed} ${failed === 1 ? 'technology' : 'technologies'}`
        );
      }

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            technology:
              selectedTechnologies[i]?.name || selectedTechnologies[i]?.key,
            error: r.reason,
          }));
        console.error(
          '[useTechnologyActionHandlers] Failed to remove tag from technologies:',
          failedResults
        );
      }
    },
    [
      selectedTechnologies,
      axios,
      invalidateTechnologies,
      setConfirmStatus,
      selectionState,
      onBulkUntagRequested, // CRITICAL: R2 fix — was missing, caused stale closure
    ]
  );

  /**
   * Opens export modal for technologies
   *
   * NOTE: Export logic now handled by UniversalExportModal.
   * This handler just opens the modal.
   */
  const handleExport = useCallback(() => {
    // Export modal will be opened by this handler if provided
    // Otherwise, it should be called directly from the action component
  }, []);

  /**
   * Shares technology URLs by copying them to clipboard
   * Generates deep links with drawer state for direct access
   *
   * Pattern matches Asset section implementation for URL consistency
   */
  const handleShare = useCallback(() => {
    try {
      const urls = selectedTechnologies.map(technology => {
        // Extract base path up to userId (e.g., "/encoded-user-id")
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const basePath = pathParts[0] ? `/${pathParts[0]}` : '';

        const params = new URLSearchParams();
        params.append('technologyDrawerKey', technology.key);
        params.append('drawerOrder', JSON.stringify(['technology']));
        return `${window.location.origin}${basePath}/technologies?${params.toString()}`;
      });

      navigator.clipboard.writeText(urls.join('\n'));

      toast.success(
        `Copied ${urls.length} technology URL${urls.length > 1 ? 's' : ''} to clipboard`
      );
    } catch (error) {
      toast.error('Failed to copy URLs to clipboard');
      console.error('[useTechnologyActionHandlers] Failed to share:', error);
    }
  }, [selectedTechnologies]);

  return {
    handleNote,
    handleAddTag,
    handleRemoveTag,
    handleExport,
    handleShare,
  };
};
